'use client';
import React, { useState, useEffect, useCallback } from 'react';
import { Shield, Users, Search, RefreshCw, LogIn, LogOut, Key, UserCheck, AlertTriangle, CheckCircle, XCircle, Clock, Filter, ChevronDown, Activity, Loader2, UserX, UserCog, Lock } from 'lucide-react';
import { createClient } from '@/lib/supabase/client';

import { toast } from 'sonner';

// ── Types ─────────────────────────────────────────────────────────────────────
interface AdminUser {
  user_id: string;
  email: string;
  full_name: string;
  role: string;
  is_active: boolean;
  password_set: boolean;
  company_id: string | null;
  company_name_ar: string | null;
  company_name_en: string | null;
  cr_number: string | null;
  created_at: string;
  last_sign_in_at: string | null;
  join_request_id: string | null;
  join_status: string | null;
  join_created_at: string | null;
  reviewer_notes: string | null;
}

interface AuthAuditLog {
  id: string;
  created_at: string;
  event_type: string;
  user_id: string | null;
  user_email: string | null;
  identifier: string | null;
  ip_address: string | null;
  status: string;
  failure_reason: string | null;
  metadata: Record<string, unknown> | null;
  company_id: string | null;
  company_name: string | null;
}

type AuthSubTab = 'users' | 'audit_logs';

// ── Helpers ───────────────────────────────────────────────────────────────────
const getUserStatusInfo = (user: AdminUser, lang: 'ar' | 'en') => {
  if (!user.is_active) return { label: lang === 'ar' ? 'موقوف' : 'Suspended', cls: 'bg-red-100 text-red-700', icon: UserX };
  if (!user.password_set) return { label: lang === 'ar' ? 'معلق' : 'Pending', cls: 'bg-amber-100 text-amber-700', icon: Clock };
  if (user.join_status === 'approved') return { label: lang === 'ar' ? 'مفعّل' : 'Approved', cls: 'bg-blue-100 text-blue-700', icon: UserCheck };
  return { label: lang === 'ar' ? 'نشط' : 'Active', cls: 'bg-green-100 text-green-700', icon: CheckCircle };
};

const eventTypeConfig: Record<string, { labelAr: string; labelEn: string; icon: React.ElementType; color: string; bg: string }> = {
  login_success:          { labelAr: 'تسجيل دخول ناجح',    labelEn: 'Login Success',          icon: LogIn,     color: 'text-green-600',  bg: 'bg-green-50' },
  login_failed:           { labelAr: 'محاولة دخول فاشلة',  labelEn: 'Login Failed',           icon: XCircle,   color: 'text-red-600',    bg: 'bg-red-50' },
  password_reset_request: { labelAr: 'طلب إعادة كلمة مرور', labelEn: 'Password Reset Request', icon: Key,       color: 'text-amber-600',  bg: 'bg-amber-50' },
  password_reset_complete:{ labelAr: 'إعادة كلمة مرور',    labelEn: 'Password Reset',         icon: Lock,      color: 'text-blue-600',   bg: 'bg-blue-50' },
  account_activation:     { labelAr: 'تفعيل حساب',         labelEn: 'Account Activation',     icon: UserCheck, color: 'text-teal-600',   bg: 'bg-teal-50' },
  logout:                 { labelAr: 'تسجيل خروج',         labelEn: 'Logout',                 icon: LogOut,    color: 'text-gray-600',   bg: 'bg-gray-50' },
  signup:                 { labelAr: 'تسجيل جديد',         labelEn: 'Sign Up',                icon: UserCog,   color: 'text-purple-600', bg: 'bg-purple-50' },
};

const getEventConfig = (type: string) =>
  eventTypeConfig[type] ?? { labelAr: type, labelEn: type, icon: Activity, color: 'text-gray-600', bg: 'bg-gray-50' };

const statusBadge = (status: string, lang: 'ar' | 'en') => {
  if (status === 'success') return <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-bold bg-green-100 text-green-700"><CheckCircle size={9} />{lang === 'ar' ? 'نجاح' : 'Success'}</span>;
  if (status === 'failed')  return <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-bold bg-red-100 text-red-700"><XCircle size={9} />{lang === 'ar' ? 'فشل' : 'Failed'}</span>;
  return <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-bold bg-amber-100 text-amber-700"><AlertTriangle size={9} />{lang === 'ar' ? 'تحذير' : 'Warning'}</span>;
};

const formatDate = (d: string | null) => {
  if (!d) return '—';
  return new Date(d).toLocaleString('en-GB', { day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' });
};

const PAGE_SIZE = 30;

// ── Main Component ────────────────────────────────────────────────────────────
export default function AuthAdminDashboard({ lang }: { lang: 'ar' | 'en' }) {
  const supabase = createClient();
  const [subTab, setSubTab] = useState<AuthSubTab>('users');

  // Users state
  const [users, setUsers] = useState<AdminUser[]>([]);
  const [usersLoading, setUsersLoading] = useState(false);
  const [userSearch, setUserSearch] = useState('');
  const [userStatusFilter, setUserStatusFilter] = useState<'all' | 'active' | 'pending' | 'suspended' | 'approved'>('all');
  const [userRoleFilter, setUserRoleFilter] = useState<string>('all');

  // Audit logs state
  const [logs, setLogs] = useState<AuthAuditLog[]>([]);
  const [logsLoading, setLogsLoading] = useState(false);
  const [logSearch, setLogSearch] = useState('');
  const [logEventFilter, setLogEventFilter] = useState<string>('all');
  const [logStatusFilter, setLogStatusFilter] = useState<string>('all');
  const [logDateFrom, setLogDateFrom] = useState('');
  const [logDateTo, setLogDateTo] = useState('');
  const [logOffset, setLogOffset] = useState(0);
  const [logHasMore, setLogHasMore] = useState(false);

  // Stats
  const [stats, setStats] = useState({ total: 0, active: 0, pending: 0, suspended: 0, approved: 0 });

  // ── Fetch users ─────────────────────────────────────────────────────────────
  const fetchUsers = useCallback(async () => {
    setUsersLoading(true);
    try {
      const { data, error } = await supabase.rpc('get_admin_users_overview');
      if (error) throw error;
      const rows = (data || []) as AdminUser[];
      setUsers(rows);

      // Compute stats
      let active = 0, pending = 0, suspended = 0, approved = 0;
      for (const u of rows) {
        if (!u.is_active) suspended++;
        else if (!u.password_set) pending++;
        else if (u.join_status === 'approved') approved++;
        else active++;
      }
      setStats({ total: rows.length, active, pending, suspended, approved });
    } catch {
      // Fallback: query user_profiles directly
      try {
        const { data } = await supabase
          .from('user_profiles')
          .select('id, email, full_name, role, is_active, password_set, company_id, created_at, companies(name_ar, name_en, cr_number)')
          .order('created_at', { ascending: false });
        if (data) {
          const mapped: AdminUser[] = data.map((u: Record<string, unknown>) => ({
            user_id: u.id as string,
            email: u.email as string,
            full_name: u.full_name as string,
            role: u.role as string,
            is_active: u.is_active as boolean,
            password_set: (u.password_set as boolean) ?? true,
            company_id: u.company_id as string | null,
            company_name_ar: (u.companies as Record<string, string> | null)?.name_ar ?? null,
            company_name_en: (u.companies as Record<string, string> | null)?.name_en ?? null,
            cr_number: (u.companies as Record<string, string> | null)?.cr_number ?? null,
            created_at: u.created_at as string,
            last_sign_in_at: null,
            join_request_id: null,
            join_status: null,
            join_created_at: null,
            reviewer_notes: null,
          }));
          setUsers(mapped);
          let active = 0, pending = 0, suspended = 0, approved = 0;
          for (const u of mapped) {
            if (!u.is_active) suspended++;
            else if (!u.password_set) pending++;
            else active++;
          }
          setStats({ total: mapped.length, active, pending, suspended, approved });
        }
      } catch {
        toast.error(lang === 'ar' ? 'تعذر تحميل بيانات المستخدمين' : 'Failed to load users');
      }
    } finally {
      setUsersLoading(false);
    }
  }, [supabase, lang]);

  // ── Fetch audit logs ────────────────────────────────────────────────────────
  const fetchLogs = useCallback(async (offset = 0, append = false) => {
    setLogsLoading(true);
    try {
      const { data, error } = await supabase.rpc('get_auth_audit_logs', {
        p_limit: PAGE_SIZE + 1,
        p_offset: offset,
        p_event_type: logEventFilter !== 'all' ? logEventFilter : null,
        p_status: logStatusFilter !== 'all' ? logStatusFilter : null,
        p_search: logSearch.trim() || null,
        p_date_from: logDateFrom ? new Date(logDateFrom).toISOString() : null,
        p_date_to: logDateTo ? new Date(logDateTo + 'T23:59:59').toISOString() : null,
      });

      if (error) throw error;
      const rows = (data || []) as AuthAuditLog[];
      const hasMore = rows.length > PAGE_SIZE;
      const trimmed = hasMore ? rows.slice(0, PAGE_SIZE) : rows;
      setLogHasMore(hasMore);
      setLogs(prev => append ? [...prev, ...trimmed] : trimmed);
      setLogOffset(offset);
    } catch {
      // Fallback: direct query
      try {
        let query = supabase
          .from('auth_audit_logs')
          .select('*')
          .order('created_at', { ascending: false })
          .range(offset, offset + PAGE_SIZE);
        if (logEventFilter !== 'all') query = query.eq('event_type', logEventFilter);
        if (logStatusFilter !== 'all') query = query.eq('status', logStatusFilter);
        if (logSearch.trim()) query = query.ilike('user_email', `%${logSearch.trim()}%`);
        const { data } = await query;
        const rows = (data || []) as AuthAuditLog[];
        setLogs(prev => append ? [...prev, ...rows] : rows);
        setLogHasMore(rows.length === PAGE_SIZE + 1);
      } catch {
        // Table may not exist yet — show empty state
        setLogs([]);
      }
    } finally {
      setLogsLoading(false);
    }
  }, [supabase, logEventFilter, logStatusFilter, logSearch, logDateFrom, logDateTo]);

  useEffect(() => { fetchUsers(); }, [fetchUsers]);
  useEffect(() => { if (subTab === 'audit_logs') fetchLogs(0); }, [subTab, fetchLogs]);

  // ── Filtered users ──────────────────────────────────────────────────────────
  const filteredUsers = users.filter(u => {
    const q = userSearch.toLowerCase();
    const matchSearch = !q || u.email?.toLowerCase().includes(q) || u.full_name?.toLowerCase().includes(q) || u.cr_number?.toLowerCase().includes(q);
    const matchRole = userRoleFilter === 'all' || u.role === userRoleFilter;
    let matchStatus = true;
    if (userStatusFilter === 'suspended') matchStatus = !u.is_active;
    else if (userStatusFilter === 'pending') matchStatus = u.is_active && !u.password_set;
    else if (userStatusFilter === 'approved') matchStatus = u.is_active && u.password_set && u.join_status === 'approved';
    else if (userStatusFilter === 'active') matchStatus = u.is_active && u.password_set;
    return matchSearch && matchRole && matchStatus;
  });

  // ── Stat cards ──────────────────────────────────────────────────────────────
  const statCards = [
    { label: lang === 'ar' ? 'إجمالي المستخدمين' : 'Total Users',  value: stats.total,     icon: Users,     bg: 'bg-[#EAF4F4]', color: 'text-[#0F5C5C]' },
    { label: lang === 'ar' ? 'نشط' : 'Active',                      value: stats.active,    icon: CheckCircle, bg: 'bg-green-50', color: 'text-green-600' },
    { label: lang === 'ar' ? 'معلق' : 'Pending',                    value: stats.pending,   icon: Clock,     bg: 'bg-amber-50',  color: 'text-amber-600' },
    { label: lang === 'ar' ? 'مفعّل' : 'Approved',                  value: stats.approved,  icon: UserCheck, bg: 'bg-blue-50',   color: 'text-blue-600' },
    { label: lang === 'ar' ? 'موقوف' : 'Suspended',                 value: stats.suspended, icon: UserX,     bg: 'bg-red-50',    color: 'text-red-600' },
  ];

  return (
    <div className="space-y-5">
      {/* Header */}
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h1 className="text-xl font-bold text-[#0D1117] flex items-center gap-2">
            <Shield size={20} className="text-[#0F5C5C]" />
            {lang === 'ar' ? 'لوحة إدارة المصادقة' : 'Authentication Admin Dashboard'}
          </h1>
          <p className="text-sm text-[#6B7280] mt-0.5">
            {lang === 'ar' ? 'إدارة المستخدمين وسجلات التدقيق الأمني' : 'Manage users and security audit logs'}
          </p>
        </div>
        <button
          onClick={() => { fetchUsers(); if (subTab === 'audit_logs') fetchLogs(0); }}
          className="flex items-center gap-1.5 h-9 px-3 bg-[#EAF4F4] text-[#0F5C5C] rounded-xl text-sm font-semibold hover:bg-[#D0E8E8] transition-colors"
        >
          <RefreshCw size={13} />
          {lang === 'ar' ? 'تحديث' : 'Refresh'}
        </button>
      </div>

      {/* Stat Cards */}
      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-3">
        {statCards.map(card => (
          <div key={card.label} className="bg-white border border-[#E2E6EA] rounded-2xl p-4 shadow-sm">
            <div className={`w-9 h-9 rounded-xl ${card.bg} flex items-center justify-center mb-2`}>
              <card.icon size={16} className={card.color} />
            </div>
            <p className="text-xl font-bold text-[#0D1117]">{card.value}</p>
            <p className="text-xs text-[#6B7280] mt-0.5">{card.label}</p>
          </div>
        ))}
      </div>

      {/* Sub-tabs */}
      <div className="flex gap-1 bg-[#F0F2F5] p-1 rounded-xl w-fit">
        {([
          { id: 'users' as AuthSubTab, labelAr: 'المستخدمون', labelEn: 'Users', icon: Users },
          { id: 'audit_logs' as AuthSubTab, labelAr: 'سجلات التدقيق', labelEn: 'Audit Logs', icon: Shield },
        ]).map(t => (
          <button
            key={t.id}
            onClick={() => setSubTab(t.id)}
            className={`flex items-center gap-1.5 px-4 py-2 rounded-lg text-sm font-semibold transition-all ${
              subTab === t.id ? 'bg-white text-[#0F5C5C] shadow-sm' : 'text-[#6B7280] hover:text-[#0D1117]'
            }`}
          >
            <t.icon size={14} />
            {lang === 'ar' ? t.labelAr : t.labelEn}
          </button>
        ))}
      </div>

      {/* ── USERS TAB ─────────────────────────────────────────────────────── */}
      {subTab === 'users' && (
        <div className="space-y-4">
          {/* Filters */}
          <div className="flex flex-wrap gap-2 items-center">
            <div className="relative flex-1 min-w-[200px] max-w-xs">
              <Search size={13} className="absolute top-1/2 -translate-y-1/2 end-3 text-[#9CA3AF]" />
              <input
                value={userSearch}
                onChange={e => setUserSearch(e.target.value)}
                placeholder={lang === 'ar' ? 'بحث بالبريد أو الاسم أو السجل التجاري...' : 'Search by email, name or CR...'}
                className="w-full h-9 bg-white border border-[#E2E6EA] rounded-xl px-3 pe-8 text-sm focus:outline-none focus:ring-2 focus:ring-[#0F5C5C]/20 focus:border-[#0F5C5C]"
              />
            </div>
            <select
              value={userStatusFilter}
              onChange={e => setUserStatusFilter(e.target.value as typeof userStatusFilter)}
              className="h-9 bg-white border border-[#E2E6EA] rounded-xl px-3 text-sm text-[#0D1117] focus:outline-none focus:ring-2 focus:ring-[#0F5C5C]/20"
            >
              <option value="all">{lang === 'ar' ? 'كل الحالات' : 'All Statuses'}</option>
              <option value="active">{lang === 'ar' ? 'نشط' : 'Active'}</option>
              <option value="pending">{lang === 'ar' ? 'معلق' : 'Pending'}</option>
              <option value="approved">{lang === 'ar' ? 'مفعّل' : 'Approved'}</option>
              <option value="suspended">{lang === 'ar' ? 'موقوف' : 'Suspended'}</option>
            </select>
            <select
              value={userRoleFilter}
              onChange={e => setUserRoleFilter(e.target.value)}
              className="h-9 bg-white border border-[#E2E6EA] rounded-xl px-3 text-sm text-[#0D1117] focus:outline-none focus:ring-2 focus:ring-[#0F5C5C]/20"
            >
              <option value="all">{lang === 'ar' ? 'كل الأدوار' : 'All Roles'}</option>
              <option value="super_admin">{lang === 'ar' ? 'مدير المنصة' : 'Super Admin'}</option>
              <option value="admin">{lang === 'ar' ? 'مدير' : 'Admin'}</option>
              <option value="manager">{lang === 'ar' ? 'مشرف' : 'Manager'}</option>
              <option value="accountant">{lang === 'ar' ? 'محاسب' : 'Accountant'}</option>
              <option value="cashier">{lang === 'ar' ? 'كاشير' : 'Cashier'}</option>
            </select>
            <span className="text-xs text-[#9CA3AF] ms-1">{filteredUsers.length} {lang === 'ar' ? 'مستخدم' : 'users'}</span>
          </div>

          {/* Users Table */}
          <div className="bg-white border border-[#E2E6EA] rounded-2xl shadow-sm overflow-hidden">
            {usersLoading ? (
              <div className="flex items-center justify-center py-16">
                <Loader2 size={24} className="animate-spin text-[#0F5C5C]" />
              </div>
            ) : filteredUsers.length === 0 ? (
              <div className="flex flex-col items-center justify-center py-16 text-[#9CA3AF]">
                <Users size={32} className="mb-2 opacity-40" />
                <p className="text-sm">{lang === 'ar' ? 'لا يوجد مستخدمون' : 'No users found'}</p>
              </div>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="border-b border-[#F0F2F5] bg-[#F7F8FA]">
                      <th className="text-start px-4 py-3 text-xs font-bold text-[#6B7280]">{lang === 'ar' ? 'المستخدم' : 'User'}</th>
                      <th className="text-start px-4 py-3 text-xs font-bold text-[#6B7280]">{lang === 'ar' ? 'الدور' : 'Role'}</th>
                      <th className="text-start px-4 py-3 text-xs font-bold text-[#6B7280]">{lang === 'ar' ? 'الحالة' : 'Status'}</th>
                      <th className="text-start px-4 py-3 text-xs font-bold text-[#6B7280]">{lang === 'ar' ? 'الشركة' : 'Company'}</th>
                      <th className="text-start px-4 py-3 text-xs font-bold text-[#6B7280]">{lang === 'ar' ? 'سجل الموافقة' : 'Approval'}</th>
                      <th className="text-start px-4 py-3 text-xs font-bold text-[#6B7280]">{lang === 'ar' ? 'آخر دخول' : 'Last Login'}</th>
                      <th className="text-start px-4 py-3 text-xs font-bold text-[#6B7280]">{lang === 'ar' ? 'تاريخ الإنشاء' : 'Created'}</th>
                    </tr>
                  </thead>
                  <tbody>
                    {filteredUsers.map(user => {
                      const statusInfo = getUserStatusInfo(user, lang);
                      const StatusIcon = statusInfo.icon;
                      return (
                        <tr key={user.user_id} className="border-b border-[#F7F8FA] hover:bg-[#FAFBFC] transition-colors">
                          <td className="px-4 py-3">
                            <div>
                              <p className="font-semibold text-[#0D1117] text-sm">{user.full_name || '—'}</p>
                              <p className="text-xs text-[#6B7280]">{user.email}</p>
                            </div>
                          </td>
                          <td className="px-4 py-3">
                            <span className="inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-bold bg-[#EAF4F4] text-[#0F5C5C]">
                              {user.role}
                            </span>
                          </td>
                          <td className="px-4 py-3">
                            <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-bold ${statusInfo.cls}`}>
                              <StatusIcon size={9} />
                              {statusInfo.label}
                            </span>
                          </td>
                          <td className="px-4 py-3">
                            {user.company_name_ar ? (
                              <div>
                                <p className="text-xs font-semibold text-[#0D1117]">{lang === 'ar' ? user.company_name_ar : (user.company_name_en || user.company_name_ar)}</p>
                                {user.cr_number && <p className="text-[10px] text-[#9CA3AF]">{user.cr_number}</p>}
                              </div>
                            ) : (
                              <span className="text-xs text-[#9CA3AF]">—</span>
                            )}
                          </td>
                          <td className="px-4 py-3">
                            {user.join_status ? (
                              <div>
                                <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-bold ${
                                  user.join_status === 'approved' ? 'bg-green-100 text-green-700' :
                                  user.join_status === 'rejected' ? 'bg-red-100 text-red-700' :
                                  user.join_status === 'pending'? 'bg-amber-100 text-amber-700' : 'bg-gray-100 text-gray-600'
                                }`}>
                                  {user.join_status === 'approved' ? (lang === 'ar' ? 'مقبول' : 'Approved') :
                                   user.join_status === 'rejected' ? (lang === 'ar' ? 'مرفوض' : 'Rejected') :
                                   user.join_status === 'pending' ? (lang === 'ar' ? 'قيد المراجعة' : 'Pending') :
                                   user.join_status}
                                </span>
                                {user.join_created_at && (
                                  <p className="text-[10px] text-[#9CA3AF] mt-0.5">{formatDate(user.join_created_at)}</p>
                                )}
                                {user.reviewer_notes && (
                                  <p className="text-[10px] text-[#6B7280] mt-0.5 max-w-[160px] truncate" title={user.reviewer_notes}>
                                    {user.reviewer_notes}
                                  </p>
                                )}
                              </div>
                            ) : (
                              <span className="text-xs text-[#9CA3AF]">—</span>
                            )}
                          </td>
                          <td className="px-4 py-3 text-xs text-[#6B7280]">{formatDate(user.last_sign_in_at)}</td>
                          <td className="px-4 py-3 text-xs text-[#6B7280]">{formatDate(user.created_at)}</td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </div>
      )}

      {/* ── AUDIT LOGS TAB ────────────────────────────────────────────────── */}
      {subTab === 'audit_logs' && (
        <div className="space-y-4">
          {/* Filters */}
          <div className="flex flex-wrap gap-2 items-center">
            <div className="relative flex-1 min-w-[200px] max-w-xs">
              <Search size={13} className="absolute top-1/2 -translate-y-1/2 end-3 text-[#9CA3AF]" />
              <input
                value={logSearch}
                onChange={e => { setLogSearch(e.target.value); }}
                onKeyDown={e => e.key === 'Enter' && fetchLogs(0)}
                placeholder={lang === 'ar' ? 'بحث بالبريد أو المعرّف...' : 'Search by email or identifier...'}
                className="w-full h-9 bg-white border border-[#E2E6EA] rounded-xl px-3 pe-8 text-sm focus:outline-none focus:ring-2 focus:ring-[#0F5C5C]/20 focus:border-[#0F5C5C]"
              />
            </div>
            <select
              value={logEventFilter}
              onChange={e => { setLogEventFilter(e.target.value); }}
              className="h-9 bg-white border border-[#E2E6EA] rounded-xl px-3 text-sm text-[#0D1117] focus:outline-none focus:ring-2 focus:ring-[#0F5C5C]/20"
            >
              <option value="all">{lang === 'ar' ? 'كل الأحداث' : 'All Events'}</option>
              <option value="login_success">{lang === 'ar' ? 'دخول ناجح' : 'Login Success'}</option>
              <option value="login_failed">{lang === 'ar' ? 'دخول فاشل' : 'Login Failed'}</option>
              <option value="password_reset_request">{lang === 'ar' ? 'طلب إعادة كلمة مرور' : 'Password Reset Request'}</option>
              <option value="password_reset_complete">{lang === 'ar' ? 'إعادة كلمة مرور' : 'Password Reset'}</option>
              <option value="account_activation">{lang === 'ar' ? 'تفعيل حساب' : 'Account Activation'}</option>
              <option value="logout">{lang === 'ar' ? 'تسجيل خروج' : 'Logout'}</option>
              <option value="signup">{lang === 'ar' ? 'تسجيل جديد' : 'Sign Up'}</option>
            </select>
            <select
              value={logStatusFilter}
              onChange={e => { setLogStatusFilter(e.target.value); }}
              className="h-9 bg-white border border-[#E2E6EA] rounded-xl px-3 text-sm text-[#0D1117] focus:outline-none focus:ring-2 focus:ring-[#0F5C5C]/20"
            >
              <option value="all">{lang === 'ar' ? 'كل الحالات' : 'All Statuses'}</option>
              <option value="success">{lang === 'ar' ? 'نجاح' : 'Success'}</option>
              <option value="failed">{lang === 'ar' ? 'فشل' : 'Failed'}</option>
              <option value="warning">{lang === 'ar' ? 'تحذير' : 'Warning'}</option>
            </select>
            <input
              type="date"
              value={logDateFrom}
              onChange={e => setLogDateFrom(e.target.value)}
              className="h-9 bg-white border border-[#E2E6EA] rounded-xl px-3 text-sm text-[#0D1117] focus:outline-none focus:ring-2 focus:ring-[#0F5C5C]/20"
              title={lang === 'ar' ? 'من تاريخ' : 'From date'}
            />
            <input
              type="date"
              value={logDateTo}
              onChange={e => setLogDateTo(e.target.value)}
              className="h-9 bg-white border border-[#E2E6EA] rounded-xl px-3 text-sm text-[#0D1117] focus:outline-none focus:ring-2 focus:ring-[#0F5C5C]/20"
              title={lang === 'ar' ? 'إلى تاريخ' : 'To date'}
            />
            <button
              onClick={() => fetchLogs(0)}
              className="h-9 px-3 bg-[#0F5C5C] text-white rounded-xl text-sm font-semibold hover:bg-[#0A3D3D] transition-colors flex items-center gap-1.5"
            >
              <Filter size={13} />
              {lang === 'ar' ? 'تطبيق' : 'Apply'}
            </button>
          </div>

          {/* Logs Table */}
          <div className="bg-white border border-[#E2E6EA] rounded-2xl shadow-sm overflow-hidden">
            {logsLoading && logs.length === 0 ? (
              <div className="flex items-center justify-center py-16">
                <Loader2 size={24} className="animate-spin text-[#0F5C5C]" />
              </div>
            ) : logs.length === 0 ? (
              <div className="flex flex-col items-center justify-center py-16 text-[#9CA3AF]">
                <Shield size={32} className="mb-2 opacity-40" />
                <p className="text-sm">{lang === 'ar' ? 'لا توجد سجلات تدقيق بعد' : 'No audit logs yet'}</p>
                <p className="text-xs mt-1">{lang === 'ar' ? 'ستظهر السجلات هنا بعد تطبيق migration قاعدة البيانات' : 'Logs will appear here after applying the database migration'}</p>
              </div>
            ) : (
              <>
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="border-b border-[#F0F2F5] bg-[#F7F8FA]">
                        <th className="text-start px-4 py-3 text-xs font-bold text-[#6B7280]">{lang === 'ar' ? 'الحدث' : 'Event'}</th>
                        <th className="text-start px-4 py-3 text-xs font-bold text-[#6B7280]">{lang === 'ar' ? 'المستخدم' : 'User'}</th>
                        <th className="text-start px-4 py-3 text-xs font-bold text-[#6B7280]">{lang === 'ar' ? 'الحالة' : 'Status'}</th>
                        <th className="text-start px-4 py-3 text-xs font-bold text-[#6B7280]">{lang === 'ar' ? 'الشركة' : 'Company'}</th>
                        <th className="text-start px-4 py-3 text-xs font-bold text-[#6B7280]">{lang === 'ar' ? 'عنوان IP' : 'IP Address'}</th>
                        <th className="text-start px-4 py-3 text-xs font-bold text-[#6B7280]">{lang === 'ar' ? 'التاريخ' : 'Date'}</th>
                      </tr>
                    </thead>
                    <tbody>
                      {logs.map(log => {
                        const cfg = getEventConfig(log.event_type);
                        const EventIcon = cfg.icon;
                        return (
                          <tr key={log.id} className="border-b border-[#F7F8FA] hover:bg-[#FAFBFC] transition-colors">
                            <td className="px-4 py-3">
                              <div className="flex items-center gap-2">
                                <div className={`w-7 h-7 rounded-lg ${cfg.bg} flex items-center justify-center shrink-0`}>
                                  <EventIcon size={13} className={cfg.color} />
                                </div>
                                <div>
                                  <p className="font-semibold text-[#0D1117] text-xs">{lang === 'ar' ? cfg.labelAr : cfg.labelEn}</p>
                                  {log.failure_reason && (
                                    <p className="text-[10px] text-red-500 mt-0.5 max-w-[180px] truncate" title={log.failure_reason}>{log.failure_reason}</p>
                                  )}
                                </div>
                              </div>
                            </td>
                            <td className="px-4 py-3">
                              <p className="text-xs font-semibold text-[#0D1117]">{log.user_email || log.identifier || '—'}</p>
                              {log.identifier && log.identifier !== log.user_email && (
                                <p className="text-[10px] text-[#9CA3AF]">{log.identifier}</p>
                              )}
                            </td>
                            <td className="px-4 py-3">{statusBadge(log.status, lang)}</td>
                            <td className="px-4 py-3 text-xs text-[#6B7280]">{log.company_name || '—'}</td>
                            <td className="px-4 py-3 text-xs text-[#9CA3AF] font-mono">{log.ip_address || '—'}</td>
                            <td className="px-4 py-3 text-xs text-[#6B7280]">{formatDate(log.created_at)}</td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
                {logHasMore && (
                  <div className="p-4 border-t border-[#F0F2F5] flex justify-center">
                    <button
                      onClick={() => fetchLogs(logOffset + PAGE_SIZE, true)}
                      disabled={logsLoading}
                      className="flex items-center gap-1.5 h-9 px-4 bg-[#EAF4F4] text-[#0F5C5C] rounded-xl text-sm font-semibold hover:bg-[#D0E8E8] transition-colors disabled:opacity-50"
                    >
                      {logsLoading ? <Loader2 size={13} className="animate-spin" /> : <ChevronDown size={13} />}
                      {lang === 'ar' ? 'تحميل المزيد' : 'Load More'}
                    </button>
                  </div>
                )}
              </>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
